import { 
  doc, collection, query, where, getDocs, runTransaction, writeBatch, updateDoc, setDoc, getDoc, serverTimestamp 
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { logAdminActionAtomic } from '@/lib/auditLogger';
import type { 
  Game, SettlementTransaction, Transfer, PlayerSettlement, User 
} from '@/lib/types';

async function requireAppAdmin(userId: string | undefined): Promise<void> {
  if (!userId) throw new Error('User not authenticated');
  const snap = await getDoc(doc(db, 'users', userId));
  if (!snap.exists()) throw new Error('User not found');
  const user = snap.data() as User;
  if (user.roles?.app_admin !== true) throw new Error('Admin role required');
}

export const deriveSettlementView = (game: Game): 'chip_counts' | 'cash_distribution' | 'digital_transfers' | 'completed' => {
  if (game.status === 'settled') return 'completed';

  if (game.status !== 'settling') return 'chip_counts';

  const settlement = game.settlement;
  if (!settlement) return 'chip_counts';

  const milestones = settlement.milestones;

  if (milestones?.settlement_completed) return 'completed';
  if (milestones?.digital_transfers_posted) return 'digital_transfers';
  if (milestones?.cash_distribution_finalized) return 'digital_transfers';

  if (milestones?.chip_counts_finalized) {
    return 'cash_distribution';
  }

  return 'chip_counts';
};


const SETTLEMENT_EPSILON = 0.01;

function roundTo50Cents(n: number): number {
  return Math.round(n * 2) / 2;
}

/**
 * Validates that cash distribution data is complete before settlement calculation.
 * Throws if total cash_received doesn't match total cash_in, which would cause
 * inflated adjusted_net values and misallocated payments.
 */
export function validateCashDistributionIntegrity(game: Game): void {
  const totalCashIn = game.players
    .filter(p => !p.is_spectator)
    .reduce((sum, p) => sum + (p.cash_in || 0), 0);

  if (totalCashIn === 0) {
    // No cash on table — no validation needed
    return;
  }

  const totalCashReceived = game.players
    .filter(p => !p.is_spectator)
    .reduce((sum, p) => {
      const inputs = game.settlement?.player_inputs[p.user_id];
      return sum + (inputs?.cash_received ?? 0);
    }, 0);

  if (Math.abs(totalCashReceived - totalCashIn) > SETTLEMENT_EPSILON) {
    throw new Error(
      `Cash distribution data incomplete: total cash received (${totalCashReceived}) does not match total cash buy-ins (${totalCashIn}). ` +
      'Please finalize cash distribution before calculating settlement.'
    );
  }
}

export function calculatePlayerSettlements(
  game: Game,
  cashDistributions: Map<string, number>
): PlayerSettlement[] {
  return game.players
    .filter(p => !p.is_spectator)
    .map(p => {
      const inputs = game.settlement?.player_inputs[p.user_id] || { final_stack: 0, cash_received: 0 };
      return {
        user_id: p.user_id,
        name: p.name,
        photo_url: p.photo_url,
        total_bought_in: p.total_bought_in,
        final_stack: inputs.final_stack,
        cash_in: p.cash_in || 0,
        cash_received: cashDistributions.get(p.user_id) ?? inputs.cash_received,
        net: inputs.final_stack - p.total_bought_in,
        settled_adjustment: p.settled_adjustment || 0,
        adjusted_net: 0,
      };
    });
}

export function calculateAdjustedNets(settlements: PlayerSettlement[]): PlayerSettlement[] {
  return settlements.map(s => ({
    ...s,
    adjusted_net: (s.net ?? 0) + s.cash_in - s.cash_received - (s.settled_adjustment || 0),
  }));
}

export function calculateMinimumTransfers(
  settlements: PlayerSettlement[]
): Transfer[] {
  const transfers: Transfer[] = [];

  const debtors = settlements
    .filter(s => s.adjusted_net < -SETTLEMENT_EPSILON)
    .map(s => ({ ...s, remaining: Math.abs(s.adjusted_net) }))
    .sort((a, b) => b.remaining - a.remaining);

  const creditors = settlements
    .filter(s => s.adjusted_net > SETTLEMENT_EPSILON)
    .map(s => ({ ...s, remaining: s.adjusted_net }))
    .sort((a, b) => b.remaining - a.remaining);

  let di = 0, ci = 0;
  while (di < debtors.length && ci < creditors.length) {
    const d = debtors[di];
    const c = creditors[ci];
    const minRem = Math.min(d.remaining, c.remaining);
    const transferAmt = roundTo50Cents(minRem);

    if (transferAmt > SETTLEMENT_EPSILON) {
      transfers.push({
        from_user_id: d.user_id,
        to_user_id: c.user_id,
        amount: transferAmt,
      });
      d.remaining -= transferAmt;
      c.remaining -= transferAmt;
    } else {
      // Cannot make a transfer of at least 0.50.
      // The player with the smaller remaining balance is effectively exhausted.
      if (d.remaining < c.remaining) di++;
      else if (c.remaining < d.remaining) ci++;
      else { di++; ci++; }
    }

    if (di < debtors.length && debtors[di].remaining < SETTLEMENT_EPSILON) di++;
    if (ci < creditors.length && creditors[ci].remaining < SETTLEMENT_EPSILON) ci++;
  }

  return transfers;
}

export async function migrateLegacySettlement(gameId: string): Promise<void> {
  const gameRef = doc(db, 'games', gameId);

  await runTransaction(db, async (transaction) => {
    const gameSnap = await transaction.get(gameRef);
    if (!gameSnap.exists()) throw new Error('Game not found');

    const game = gameSnap.data() as Game;

    const gameAny = game as any;
    const hasLegacySettlementFields = Boolean(gameAny.settlement_calculated_at || gameAny.settlement_finalized_at);
    const hasSettlementInputs = Object.keys(game.settlement?.player_inputs || {}).length > 0;

    // Empty player_inputs are valid for modern chip-entry games before counts are entered.
    // Only backfill them when a legacy timestamp field proves this is an old settlement shape.
    const shouldMigrate = (
      !game.settlement || 
      (hasLegacySettlementFields && !hasSettlementInputs)
    ) && ['chip_entry', 'settling', 'settled'].includes(game.status);

    if (!shouldMigrate) return;

    const playerSnapshot = game.players.map(p => ({
      userId: p.user_id,
      name: p.name,
      buyIn: p.total_bought_in,
      digitalIn: p.digital_in,
      cashIn: p.cash_in,
    }));

    const playerInputs: Record<string, { final_stack: number; cash_received: number }> = {};
    game.players.forEach(p => {
      const existing = game.settlement?.player_inputs?.[p.user_id];
      playerInputs[p.user_id] = {
        final_stack: existing?.final_stack ?? 0,
        cash_received: existing?.cash_received ?? 0,
      };
    });

    // Auto-populate left_early player data
    game.players.forEach(p => {
      if (p.left_early) {
        playerInputs[p.user_id] = {
          final_stack: p.left_stack,
          cash_received: p.left_cash,
        };
      }
    });

    const milestones: any = {};
    if (gameAny.settlement_calculated_at) {
      milestones.chip_counts_finalized = {
        finalized_at: gameAny.settlement_calculated_at,
      };
    }
    if (gameAny.settlement_finalized_at) {
      milestones.digital_transfers_posted = {
        posted_at: gameAny.settlement_finalized_at,
      };
      milestones.settlement_completed = {
        completed_at: gameAny.settlement_finalized_at,
      };
    }

    const settlement = {
      gameId,
      player_snapshot: playerSnapshot,
      player_inputs: playerInputs,
      milestones,
      updatedAt: serverTimestamp(),
      status: 'active' as const,
    };

    const updateFields: any = {
      settlement,
    };

    if (gameAny.settlement_finalized_at) {
      updateFields.status = 'settled';
    }

    transaction.update(gameRef, updateFields);

    console.log(`Migrated legacy settlement for game ${gameId}`);
  });
}

export async function updateCashReceived(
  gameId: string,
  playerUserId: string,
  cash_received: number
): Promise<void> {
  const gameRef = doc(db, 'games', gameId);

  await runTransaction(db, async (transaction) => {
    const gameSnap = await transaction.get(gameRef);
    if (!gameSnap.exists()) throw new Error('Game not found');

    const game = gameSnap.data() as Game;
    if (!game.settlement) throw new Error('Settlement not initialized');
    
    const playerInputs = { ...game.settlement.player_inputs };
    playerInputs[playerUserId] = {
      ...playerInputs[playerUserId],
      cash_received: cash_received,
    };

    transaction.update(gameRef, { 
      'settlement.player_inputs': playerInputs 
    });
  });
}

export async function updateAllCashReceived(
  gameId: string,
  cashUpdates: Record<string, number>
): Promise<void> {
  const gameRef = doc(db, 'games', gameId);

  await runTransaction(db, async (transaction) => {
    const gameSnap = await transaction.get(gameRef);
    if (!gameSnap.exists()) throw new Error('Game not found');

    const game = gameSnap.data() as Game;
    if (!game.settlement) throw new Error('Settlement not initialized');

    const playerInputs = { ...game.settlement.player_inputs };
    game.players.forEach(player => {
      if (cashUpdates[player.user_id] !== undefined) {
        playerInputs[player.user_id] = {
          ...playerInputs[player.user_id],
          cash_received: cashUpdates[player.user_id],
        };
      }
    });

    transaction.update(gameRef, { 
      'settlement.player_inputs': playerInputs 
    });
  });
}

async function _calculateSettlementTransaction(
  transaction: any,
  gameId: string,
  adminUserId: string,
  game: Game,
  idempotencyKey: string
): Promise<void> {
  const existingTxsQuery = query(collection(db, 'settlement_transactions'), where('game_id', '==', gameId));
  const existingTxsSnap = await getDocs(existingTxsQuery);

  existingTxsSnap.docs.forEach(docSnap => {
    transaction.delete(docSnap.ref);
  });

  // 1. Validate cash_received data integrity before calculating settlements
  validateCashDistributionIntegrity(game);

  // 2. Prepare settlements
  const cashDistributions = new Map<string, number>();
  game.players.forEach(p => {
    const inputs = game.settlement?.player_inputs[p.user_id];
    if (inputs && inputs.cash_received !== undefined) {
      cashDistributions.set(p.user_id, inputs.cash_received);
    }
  });

  const settlements = calculatePlayerSettlements(game, cashDistributions);
  const adjusted = calculateAdjustedNets(settlements);
  const transfers = calculateMinimumTransfers(adjusted);

  // 3. Validate zero-sum
  const netSum = adjusted.reduce((sum, s) => sum + s.adjusted_net, 0);
  if (Math.abs(netSum) > SETTLEMENT_EPSILON * adjusted.length) {
    console.warn(`Settlement imbalance: ${netSum.toFixed(2)}`);
  }

  // 4. Write transactions
  transfers.forEach(t => {
    const txRef = doc(collection(db, 'settlement_transactions'));
    transaction.set(txRef, {
      id: txRef.id,
      game_id: gameId,
      from_user_id: t.from_user_id,
      to_user_id: t.to_user_id,
      amount: t.amount,
      beem_link: null,
      status: 'pending',
      created_at: serverTimestamp(),
      notified_at: null,
      settled_at: null,
      settled_by: null,
      payment_method: null,
      payment_method_entered_at: null,
      idempotency_key: idempotencyKey,
      message_id: null,
      payer_confirmed: false,
      payee_confirmed: false,
      payer_confirmed_at: null,
      payer_confirmed_by_telegram_id: null,
      payee_confirmed_at: null,
      payee_confirmed_by_telegram_id: null,
      confirmation_source: null,
    });
  });

  // 5. Update game status
    transaction.update(doc(db, 'games', gameId), {
      status: 'settling',
      zero_sum_confirmed: true,
    });

  // 6. Idempotency & Audit
  const idempotencyRef = doc(db, 'idempotency_keys', idempotencyKey);
  transaction.set(idempotencyRef, { status: 'completed', timestamp: serverTimestamp() });
  logAdminActionAtomic(transaction, 'settle_all_transactions', adminUserId, { transfer_count: transfers.length }, gameId, null);
}

export async function calculateSettlement(
  gameId: string,
  adminUserId: string
): Promise<string> {
  const settlementVersion = 1; // In a real app, this might be tracked in the Game doc
  const idempotencyKey = `settlement_${gameId}_v${settlementVersion}`;
  
  const failureLogRef = doc(collection(db, 'settlement_attempts'));
  await setDoc(failureLogRef, {
    game_id: gameId,
    timestamp: serverTimestamp(),
    status: 'pending_failure',
    attempted_by: adminUserId,
    transfer_count: 0
  });

  try {
    await runTransaction(db, async (transaction) => {
      const gameSnap = await transaction.get(doc(db, 'games', gameId));
      if (!gameSnap.exists()) throw new Error('Game not found');
      const game = gameSnap.data() as Game;

      await _calculateSettlementTransaction(transaction, gameId, adminUserId, game, idempotencyKey);

      transaction.update(failureLogRef, { status: 'recorded' });
    });
  } catch (e: any) {
    await updateDoc(failureLogRef, { 
      status: 'failed', 
      error_message: e.message,
      error_code: e.code || 'UNKNOWN'
    });
    throw e;
  }

  return gameId;
}

export async function toggleTransactionSettled(
  transactionId: string,
  userId: string,
  isCurrentlySettled: boolean
): Promise<void> {
  await updateDoc(doc(db, 'settlement_transactions', transactionId), {
    status: isCurrentlySettled ? 'pending' : 'settled',
    settled_at: isCurrentlySettled ? null : serverTimestamp(),
    settled_by: isCurrentlySettled ? null : userId,
  });
}



export async function markAllTransactionsSettled(
  gameId: string,
  adminUserId: string
): Promise<void> {
  const q = query(collection(db, 'settlement_transactions'), 
    where('game_id', '==', gameId), 
    where('status', '==', 'pending')
  );
  const snapshot = await getDocs(q);
  
   const batch = writeBatch(db);
  snapshot.docs.forEach(docSnap => {
    batch.update(docSnap.ref, { 
      status: 'settled', 
      settled_at: serverTimestamp(), 
      settled_by: adminUserId 
    });
  });
  
  await batch.commit();
}

export async function endGame(gameId: string): Promise<string> {
  const gameRef = doc(db, 'games', gameId);

  await runTransaction(db, async (transaction) => {
    const gameSnap = await transaction.get(gameRef);
    if (!gameSnap.exists()) throw new Error('Game not found');

    const game = gameSnap.data() as Game;
    if (game.status !== 'active') {
      throw new Error('Game is not active and cannot be ended');
    }

    const playerSnapshot = game.players.map(p => ({
      userId: p.user_id,
      name: p.name,
      buyIn: p.total_bought_in,
      digitalIn: p.digital_in,
      cashIn: p.cash_in,
    }));

    const playerInputs: Record<string, { final_stack: number; cash_received: number }> = {};
    game.players.forEach(p => {
      if (p.left_early) {
        playerInputs[p.user_id] = {
          final_stack: p.left_stack,
          cash_received: p.left_cash,
        };
      }
    });

    const settlement = {
      gameId,
      player_snapshot: playerSnapshot,
      player_inputs: playerInputs,
      milestones: {},
      updatedAt: serverTimestamp(),
      status: 'active' as const,
    };

    transaction.update(gameRef, {
      status: 'chip_entry',
      end_time: serverTimestamp(),
      settlement,
    });
  });

  return gameId;
}

export async function settleAllTransactions(
  gameId: string,
  adminUserId: string
): Promise<void> {
  const q = query(collection(db, 'settlement_transactions'), 
    where('game_id', '==', gameId), 
    where('status', '==', 'pending')
  );
  const snapshot = await getDocs(q);
  
   const batch = writeBatch(db);
  snapshot.docs.forEach(docSnap => {
    batch.update(docSnap.ref, { 
      status: 'settled', 
      settled_at: serverTimestamp(), 
      settled_by: adminUserId 
    });
  });
  
  batch.update(doc(db, 'games', gameId), {
    status: 'settled',
  });

  await batch.commit();
}

export async function reopenGame(gameId: string, userId: string): Promise<void> {
  await requireAppAdmin(userId);

  const gameRef = doc(db, 'games', gameId);
  const gameSnap = await getDoc(gameRef);
  if (!gameSnap.exists()) throw new Error('Game not found');

  const game = gameSnap.data() as Game;

  if (game.status !== 'settled') throw new Error('Only settled games can be reopened');

  const txQuery = query(collection(db, 'settlement_transactions'), where('game_id', '==', gameId));
  const txSnapshot = await getDocs(txQuery);
  const batch = writeBatch(db);

  const adjustments: Record<string, number> = {};
  txSnapshot.docs.forEach(docSnap => {
    const tx = docSnap.data() as SettlementTransaction;
    batch.update(docSnap.ref, { status: 'archived' });

    if (tx.status === 'settled') {
      adjustments[tx.from_user_id] = (adjustments[tx.from_user_id] || 0) - tx.amount;
      adjustments[tx.to_user_id] = (adjustments[tx.to_user_id] || 0) + tx.amount;
    }
  });

  const players = game.players.map(p => ({
    ...p,
    settled_adjustment: adjustments[p.user_id] || 0,
  }));

  const playerSnapshot = players.map(p => ({
    userId: p.user_id,
    name: p.name,
    buyIn: p.total_bought_in,
    digitalIn: p.digital_in,
    cashIn: p.cash_in,
    settled_adjustment: p.settled_adjustment || 0,
  }));

  batch.update(gameRef, {
    status: 'chip_entry',
    reopened_at: serverTimestamp(),
    reopened_by: userId,
    force_settled: false,
    force_settle_discrepancy: null,
    force_settle_reason: null,
    force_settled_by: null,
    players,
    settlement: {
      ...game.settlement,
      player_inputs: {},
      milestones: {},
      player_snapshot: playerSnapshot,
      updatedAt: serverTimestamp(),
    },
  });

  logAdminActionAtomic(batch, 'reopen_game', userId, { reopened: true }, gameId, null);
  await batch.commit();
}

export async function resetSettlement(gameId: string): Promise<void> {
  const gameRef = doc(db, 'games', gameId);
  const gameSnap = await getDoc(gameRef);
  if (!gameSnap.exists()) throw new Error('Game not found');

  await updateDoc(gameRef, {
    'settlement.player_inputs': {},
    'settlement.milestones': {},
    status: 'chip_entry',
  });
}

export async function finalizeChipCounts(
  gameId: string,
  adminUserId: string,
  final_stacks: Record<string, number>,
  forceSettleConfirmed: boolean = false,
): Promise<void> {
  await requireAppAdmin(adminUserId);

  const gameRef = doc(db, 'games', gameId);

  await runTransaction(db, async (transaction) => {
    await _finalizeChipCountsTransaction(transaction, gameRef, gameId, adminUserId, final_stacks, forceSettleConfirmed);
  });
}

async function _finalizeChipCountsTransaction(
  transaction: any,
  gameRef: any,
  gameId: string,
  adminUserId: string,
  final_stacks: Record<string, number>,
  forceSettleConfirmed: boolean,
): Promise<Game> {
  const gameSnap = await transaction.get(gameRef);
  if (!gameSnap.exists()) throw new Error('Game not found');

  let game = gameSnap.data() as Game;

   if (game.status !== 'chip_entry' && game.status !== 'settling') {
     throw new Error('Game must be in chip_entry or settling status to finalize chip counts');
   }

  let settlement = game.settlement;

  if (!settlement) {
    const playerSnapshot = game.players.map(p => ({
      userId: p.user_id,
      name: p.name,
      buyIn: p.total_bought_in,
      digitalIn: p.digital_in,
      cashIn: p.cash_in,
    }));

    settlement = {
      gameId,
      player_snapshot: playerSnapshot,
      player_inputs: {},
      milestones: {},
      updatedAt: serverTimestamp(),
      status: 'active' as const,
    };

    transaction.update(gameRef, { settlement });
    game = { ...game, settlement };
  }

   const playerInputs = { ...settlement.player_inputs };

   // Pre-populate left_early player defaults if not already in playerInputs
   game.players.forEach(p => {
     if (p.left_early && playerInputs[p.user_id] === undefined) {
       playerInputs[p.user_id] = {
         final_stack: p.left_stack,
         cash_received: p.left_cash,
       };
     }
   });

   Object.entries(final_stacks).forEach(([userId, stack]) => {
     playerInputs[userId] = {
       ...playerInputs[userId],
       final_stack: stack,
     };
   });

  const activePlayers = game.players.filter(p => !p.is_spectator);
  const totalFinalStack = activePlayers.reduce(
    (acc, player) => acc + (playerInputs[player.user_id]?.final_stack ?? 0),
    0,
  );
  const totalPot = game.settlement?.player_snapshot.reduce((sum, p) => sum + (p.buyIn || 0), 0) || 0;
  const chipCountTolerance = activePlayers.length * 0.5;

  if (Math.abs(totalFinalStack - totalPot) > chipCountTolerance && !forceSettleConfirmed) {
    throw new Error('Total chip counts do not match the pot');
  }

  const chipCountsMilestone = {
    finalized_at: serverTimestamp(),
    total_final_stack: totalFinalStack,
    total_pot_snapshot: totalPot,
    force_settle_confirmed: forceSettleConfirmed,
  };
  const updatedAt = serverTimestamp();

  transaction.update(gameRef, {
    status: 'settling',
    players: game.players,
    'settlement.player_inputs': playerInputs,
    'settlement.milestones.chip_counts_finalized': chipCountsMilestone,
    'settlement.updatedAt': updatedAt,
  });

  logAdminActionAtomic(transaction, 'finalize_chip_counts', adminUserId, {
    total_final_stack: totalFinalStack,
    total_pot: totalPot,
    force_settle: forceSettleConfirmed,
  }, gameId, null);

  return {
    ...game,
    status: 'settling',
    settlement: {
      ...settlement,
      gameId,
      player_inputs: playerInputs,
      milestones: {
        ...settlement.milestones,
        chip_counts_finalized: chipCountsMilestone,
      },
      updatedAt,
    },
  };
}

export async function finalizeChipCountsAndPostNoCashSettlement(
  gameId: string,
  adminUserId: string,
  final_stacks: Record<string, number>
): Promise<void> {
  await requireAppAdmin(adminUserId);

  const gameRef = doc(db, 'games', gameId);

  await runTransaction(db, async (transaction) => {
    // 1. Finalize chip counts
    const game = await _finalizeChipCountsTransaction(transaction, gameRef, gameId, adminUserId, final_stacks, false);

    if ((game.settlement?.player_snapshot.reduce((sum, p) => sum + (p.cashIn || 0), 0) || 0) === 0) {
      // 2. Fast-track no-cash settlement
      const cashDistributions = new Map<string, number>();
      game.players.forEach(p => {
        const inputs = game.settlement?.player_inputs[p.user_id];
        if (inputs && inputs.cash_received !== undefined) {
          cashDistributions.set(p.user_id, inputs.cash_received);
        }
      });

      const settlements = calculatePlayerSettlements(game, cashDistributions);
      const adjusted = calculateAdjustedNets(settlements);
      const transfers = calculateMinimumTransfers(adjusted);

      transfers.forEach(t => {
        const txRef = doc(collection(db, 'settlement_transactions'));
        transaction.set(txRef, {
          id: txRef.id,
          game_id: gameId,
          from_user_id: t.from_user_id,
          to_user_id: t.to_user_id,
          amount: t.amount,
          beem_link: null,
          status: 'pending',
          created_at: serverTimestamp(),
          notified_at: null,
          settled_at: null,
          settled_by: null,
          payment_method: null,
          payment_method_entered_at: null,
          idempotency_key: `fast_settle_${gameId}`,
          message_id: null,
          payer_confirmed: false,
          payee_confirmed: false,
          payer_confirmed_at: null,
          payer_confirmed_by_telegram_id: null,
          payee_confirmed_at: null,
          payee_confirmed_by_telegram_id: null,
          confirmation_source: null,
        });
      });

      transaction.update(gameRef, {
        status: 'settled',
        'settlement.milestones.digital_transfers_posted': {
          posted_at: serverTimestamp(),
          transaction_ids: [], // We are using collection, but let's set it for milestone
          transfer_count: transfers.length,
          zero_transfer: transfers.length === 0,
        },
        'settlement.milestones.settlement_completed': {
          completed_at: serverTimestamp(),
        },
      });

      logAdminActionAtomic(transaction, 'fast_settle_no_cash', adminUserId, { transfer_count: transfers.length }, gameId, null);
    }
  });
}

export async function finalizeCashDistribution(
  gameId: string,
  adminUserId: string,
  cash_received: Record<string, number>
): Promise<void> {
  await requireAppAdmin(adminUserId);

  const gameRef = doc(db, 'games', gameId);

  await runTransaction(db, async (transaction) => {
    const gameSnap = await transaction.get(gameRef);
    if (!gameSnap.exists()) throw new Error('Game not found');
    const game = gameSnap.data() as Game;

    if (game.status !== 'settling') {
      throw new Error('Game must be in settling status to finalize cash distribution');
    }

    // Update player inputs with received cash
    const playerInputs = { ...game.settlement!.player_inputs };
    Object.entries(cash_received).forEach(([userId, amount]) => {
      playerInputs[userId] = {
        ...playerInputs[userId],
        cash_received: amount,
      };
    });

    // Zero-sum check
    const activePlayers = game.players.filter(p => !p.is_spectator);
    const totalCashReceived = activePlayers.reduce(
      (acc, player) => acc + (playerInputs[player.user_id]?.cash_received ?? 0),
      0,
    );
    const totalCashBuyIns = game.total_cash ?? (
      game.settlement?.player_snapshot.reduce((sum, p) => sum + (p.cashIn || 0), 0) || 0
    );
    if (Math.abs(totalCashReceived - totalCashBuyIns) > SETTLEMENT_EPSILON) {
      throw new Error('Total cash received does not match the total cash buy-ins');
    }

    // Set milestone
    transaction.update(gameRef, {
      'settlement.player_inputs': playerInputs,
      'settlement.milestones.cash_distribution_finalized': {
        finalized_at: serverTimestamp(),
        total_cash_received: totalCashReceived,
        total_cash_snapshot: totalCashBuyIns,
      },
      'settlement.updatedAt': serverTimestamp(),
    });

    // Trigger calculation and post digital transfers
    const idempotencyKey = `settlement_finalized_${gameId}`;
    await _calculateSettlementTransaction(transaction, gameId, adminUserId, {
      ...game,
      settlement: {
        ...game.settlement!,
        player_inputs: playerInputs,
      },
    }, idempotencyKey);

    logAdminActionAtomic(transaction, 'finalize_cash_distribution', adminUserId, {
      total_cash_received: totalCashReceived,
    }, gameId, null);
  });
}

export async function postDigitalTransfers(
  gameId: string,
  adminUserId: string
): Promise<void> {
  await requireAppAdmin(adminUserId);

  const gameRef = doc(db, 'games', gameId);

  await runTransaction(db, async (transaction) => {
    const gameSnap = await transaction.get(gameRef);
    if (!gameSnap.exists()) throw new Error('Game not found');
    const game = gameSnap.data() as Game;

    if (game.status !== 'settling') {
      throw new Error('Game must be in settling status to post digital transfers');
    }

    if (!game.settlement?.milestones.cash_distribution_finalized) {
      throw new Error('Cash distribution must be finalized before posting digital transfers');
    }

    const txQuery = query(
      collection(db, 'settlement_transactions'),
      where('game_id', '==', gameId),
      where('status', '==', 'pending')
    );
    const txSnapshot = await getDocs(txQuery);
    const transactionIds = txSnapshot.docs.map(docSnap => docSnap.id);

    transaction.update(gameRef, {
      status: 'settling',
      'settlement.updatedAt': serverTimestamp(),
      'settlement.milestones.digital_transfers_posted': {
        posted_at: serverTimestamp(),
        transaction_ids: transactionIds,
        transfer_count: transactionIds.length,
        zero_transfer: transactionIds.length === 0,
      },
    });

    logAdminActionAtomic(transaction, 'post_digital_transfers', adminUserId, {
      transfer_count: transactionIds.length,
    }, gameId, null);
  });
}

export async function confirmDigitalTransfers(
  gameId: string,
  adminUserId: string
): Promise<void> {
  await requireAppAdmin(adminUserId);

  const gameRef = doc(db, 'games', gameId);

  await runTransaction(db, async (transaction) => {
    const gameSnap = await transaction.get(gameRef);
    if (!gameSnap.exists()) throw new Error('Game not found');

    transaction.update(gameRef, {
      'settlement.milestones.settlement_completed': {
        completed_at: serverTimestamp(),
      },
      'settlement.updatedAt': serverTimestamp(),
    });

    logAdminActionAtomic(transaction, 'confirm_digital_transfers', adminUserId, {
      completed: true,
    }, gameId, null);
  });
}

export async function unlockStep(
  gameId: string,
  adminUserId: string,
  targetView: 'chip_counts' | 'cash_distribution' | 'digital_transfers'
): Promise<void> {
  await requireAppAdmin(adminUserId);

  const gameRef = doc(db, 'games', gameId);

  await runTransaction(db, async (transaction) => {
    const gameSnap = await transaction.get(gameRef);
    if (!gameSnap.exists()) throw new Error('Game not found');
    const game = gameSnap.data() as Game;

    if (game.status === 'settled') {
      throw new Error('Cannot unlock steps of a settled game. Please reopen the game first.');
    }

    const milestones = { ...game.settlement!.milestones };

    if (targetView === 'chip_counts') {
      milestones.chip_counts_finalized = null;
      milestones.cash_distribution_finalized = null;
      milestones.digital_transfers_posted = null;
      milestones.settlement_completed = null;
    } else if (targetView === 'cash_distribution') {
      milestones.cash_distribution_finalized = null;
      milestones.digital_transfers_posted = null;
      milestones.settlement_completed = null;
    } else if (targetView === 'digital_transfers') {
      milestones.digital_transfers_posted = null;
      milestones.settlement_completed = null;
    }

    transaction.update(gameRef, {
      'settlement.milestones': milestones,
      'settlement.updatedAt': serverTimestamp(),
    });

    logAdminActionAtomic(transaction, 'unlock_settlement_step', adminUserId, { targetView }, gameId, null);
  });
}

